"use client";

import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { DEVELOPERS, HOME, PRODUCT, SOLUTIONS, TEST_KEY } from "./links";
import { SOLUTION_ICONS } from "./solution-icons";
import { FamilyLogo } from "./Logo";

function Caret() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}

/**
 * The family's nav, from its landing: clear at the top and solid once scrolled (never a backdrop blur), out of
 * the way while you read down, back when you scroll up; the Solutions menu; and a sheet on phones. The docs
 * keep it `solid` (always there, always opaque) and add their own tools beside the call to action (`extra`).
 * The console, where test keys are made, leaves the call to action out (`testKey={false}`).
 */
export function FamilyNav({
  solid = false,
  extra,
  testKey = true,
}: {
  solid?: boolean;
  extra?: ReactNode;
  /** "Get a test key", in the bar and in the phone's sheet. */
  testKey?: boolean;
} = {}) {
  const [stuck, setStuck] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [sheetSolutions, setSheetSolutions] = useState(false);
  const dd = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    let lastY = window.scrollY;
    let travel = 0;
    let queued = false;
    const onScroll = () => {
      const y = window.scrollY;
      const d = y - lastY;
      lastY = y;
      setStuck(y > 10);
      if (y < 500 || openRef.current) {
        setHidden(false);
        travel = 0;
        return;
      }
      if (!d) return;
      travel = d > 0 === travel > 0 ? travel + d : d;
      if (travel > 24) setHidden(true);
      else if (travel < -24) setHidden(false);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        onScroll();
      });
    };
    onScroll();
    window.addEventListener("scroll", queue, { passive: true });
    return () => window.removeEventListener("scroll", queue);
  }, []);

  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (!dd.current?.contains(e.target as Node)) setMenu(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(false);
    };
    const wide = window.matchMedia("(min-width:961px)");
    const closeSheet = (e: MediaQueryListEvent) => {
      if (e.matches) setOpen(false);
    };
    document.addEventListener("click", away);
    document.addEventListener("keydown", esc);
    wide.addEventListener("change", closeSheet);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", esc);
      wide.removeEventListener("change", closeSheet);
    };
  }, []);

  const navClass = [
    "nav",
    (stuck || solid) && "stuck",
    hidden && !open && !solid && "hide",
    open && "open",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header className={navClass}>
        <div className="wrap">
          <FamilyLogo href={HOME} />
          <nav aria-label="Primary">
            {PRODUCT.map((l) => (
              <a key={l.label} href={l.href}>
                {l.label}
              </a>
            ))}
            <div className={menu ? "nav-dd open" : "nav-dd"} ref={dd}>
              <button
                type="button"
                className="dd-btn"
                aria-haspopup="true"
                aria-expanded={menu}
                onClick={() => setMenu((m) => !m)}
              >
                Solutions
                <Caret />
              </button>
              <div className="dd-menu" role="menu" aria-label="Solutions">
                {SOLUTIONS.map((s) => (
                  <a
                    key={s.slug}
                    className={
                      s.slug === "enterprise" ? "dd-item wide" : "dd-item"
                    }
                    role="menuitem"
                    href={s.href}
                  >
                    <span className="dd-ic">
                      <svg viewBox="0 0 256 256" aria-hidden="true">
                        {SOLUTION_ICONS[s.slug]?.map((d) => (
                          <path key={d} d={d} />
                        ))}
                      </svg>
                    </span>
                    <span>
                      <b>{s.label}</b>
                      <small>{s.says}</small>
                    </span>
                  </a>
                ))}
              </div>
            </div>
            <Link href={DEVELOPERS} aria-current="page">
              Developers
            </Link>
          </nav>
          <div className="right">
            {extra}
            {testKey ? (
              <Link className="btn btn-ink btn-sm mag" href={TEST_KEY}>
                Get a test key
              </Link>
            ) : null}
            <button
              type="button"
              className="burger"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => {
                setOpen((o) => !o);
                setSheetSolutions(false);
              }}
            >
              <i />
              <i />
            </button>
          </div>
        </div>
      </header>
      <div className="sheet-menu" inert={!open}>
        {PRODUCT.map((l) => (
          <a key={l.label} href={l.href}>
            {l.label}
          </a>
        ))}
        <button
          type="button"
          className="sm-dd"
          aria-expanded={sheetSolutions}
          aria-controls="vf-sheet-solutions"
          onClick={() => setSheetSolutions((s) => !s)}
        >
          Solutions
          <Caret />
        </button>
        <div className="sm-sol" id="vf-sheet-solutions">
          <div>
            {SOLUTIONS.map((s) => (
              <a key={s.slug} className="sm-sub" href={s.href}>
                {s.label}
              </a>
            ))}
          </div>
        </div>
        <Link href={DEVELOPERS} aria-current="page">
          Developers
        </Link>
        {testKey ? (
          <Link className="btn btn-ink" href={TEST_KEY}>
            Get a test key
          </Link>
        ) : null}
      </div>
    </>
  );
}
